import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import currentDomain from '../../Utils/Utils';

// crypto.randomUUID is undefined outside a secure context, and the show-day
// tablets run this over plain http. Same fallback as the registration form.
const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `check-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const today = () => new Date().toISOString().slice(0, 10);

/*
Records one physical check.

The payer and the cars are picked independently on purpose: a check written by
one person routinely covers cars registered under someone else's name (spouses
entering separately). So the car list is every unpaid car in the show, not just
the payer's.
*/
const AddCheckModal = ({ isOpen, onClose, onSaved, judge_uuid }) => {
  const [payers, setPayers] = useState([]);
  const [unpaidCars, setUnpaidCars] = useState([]);
  const [entryFee, setEntryFee] = useState(25);

  const [payerQuery, setPayerQuery] = useState('');
  const [selectedPayer, setSelectedPayer] = useState(null);
  const [amount, setAmount] = useState('');
  const [dateReceived, setDateReceived] = useState(today());
  const [carQuery, setCarQuery] = useState('');
  const [selectedCarIds, setSelectedCarIds] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const idempotencyKeyRef = useRef(newIdempotencyKey());

  const fetchLists = async () => {
    setIsLoading(true);
    try {
      const [payersRes, carsRes, feeRes] = await Promise.all([
        fetch(`${currentDomain(location)}/api/payments/payers`),
        fetch(`${currentDomain(location)}/api/payments/unpaid`),
        fetch(`${currentDomain(location)}/api/config/entryfee`),
      ]);
      if (!payersRes.ok || !carsRes.ok) throw new Error('Failed to load payment data');
      setPayers(await payersRes.json());
      setUnpaidCars(await carsRes.json());
      if (feeRes.ok) setEntryFee((await feeRes.json()).entryFee);
    } catch (error) {
      toast.error('Failed to load registrants and unpaid cars.');
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to a blank check every time the modal opens, including a fresh
  // idempotency key so a second check is not mistaken for a replay of the first.
  useEffect(() => {
    if (!isOpen) return;
    setPayerQuery('');
    setSelectedPayer(null);
    setAmount('');
    setDateReceived(today());
    setCarQuery('');
    setSelectedCarIds([]);
    idempotencyKeyRef.current = newIdempotencyKey();
    fetchLists();
  }, [isOpen]);

  const filteredPayers = useMemo(() => {
    const q = payerQuery.trim().toLowerCase();
    if (!q) return payers.slice(0, 8);
    return payers
      .filter((p) =>
        `${p.fname} ${p.lname}`.toLowerCase().includes(q) || (p.phone || '').includes(q)
      )
      .slice(0, 8);
  }, [payers, payerQuery]);

  // Ticked cars stay pinned at the top, so narrowing the filter never hides
  // something already added to the check.
  const visibleCars = useMemo(() => {
    const q = carQuery.trim().toLowerCase();
    const matches = (c) =>
      !q ||
      String(c.car_number).includes(q) ||
      `${c.owner_first_name} ${c.owner_last_name}`.toLowerCase().includes(q) ||
      `${c.year} ${c.make} ${c.model}`.toLowerCase().includes(q) ||
      (c.owner_phone || '').includes(q);

    const selected = unpaidCars.filter((c) => selectedCarIds.includes(c.car_id));
    const rest = unpaidCars.filter((c) => !selectedCarIds.includes(c.car_id) && matches(c));
    return [...selected, ...rest];
  }, [unpaidCars, carQuery, selectedCarIds]);

  const toggleCar = (carId) => {
    setSelectedCarIds((prev) =>
      prev.includes(carId) ? prev.filter((id) => id !== carId) : [...prev, carId]
    );
  };

  const expected = selectedCarIds.length * entryFee;
  const enteredAmount = Number(amount);
  const amountIsValid = Number.isFinite(enteredAmount) && enteredAmount > 0;
  // A mismatch is a warning, not a blocker: people round up, add a donation,
  // or underpay, and the admin is looking at the physical check.
  const amountMatches = amountIsValid && enteredAmount === expected;
  const canSave = !!selectedPayer && amountIsValid && selectedCarIds.length > 0 && !submitting;

  const handleSave = async () => {
    if (!canSave) return;
    if (!judge_uuid) {
      toast.error('Unable to determine logged in judge. Please log in again.');
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    try {
      const response = await fetch(`${currentDomain(location)}/api/payments/checks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKeyRef.current,
        },
        body: JSON.stringify({
          registrantUUID: selectedPayer.uuid,
          amount: enteredAmount,
          dateReceived,
          enteredBy: judge_uuid,
          carUUIDs: selectedCarIds,
        }),
      });

      const raw = await response.text();
      if (!response.ok) {
        toast.error(`Failed to save check. ${raw}`);
        return;
      }
      const body = raw ? JSON.parse(raw) : {};

      // carsPaid can fall short of carsRequested if someone else paid for one
      // of these cars between loading the list and saving.
      if (body.carsPaid < body.carsRequested) {
        toast.warn(`Check saved, but only ${body.carsPaid} of ${body.carsRequested} cars were marked paid. The others were already paid.`);
      } else {
        toast.success(`Check saved. ${body.carsPaid} car(s) marked paid.`);
      }
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(`Failed to save check. ${error?.message ?? error}`);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Background Overlay */}
      <div className="fixed inset-0 bg-black opacity-50"></div>

      {/* Modal Content */}
      <div className="bg-white p-6 rounded-lg shadow-lg z-10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="font-bold text-xl mb-4">Add Check</h2>

        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <>
            {/* Payer */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Who wrote the check?
              </label>
              {selectedPayer ? (
                <div className="flex items-center justify-between border border-gray-300 rounded p-2">
                  <span>
                    {selectedPayer.fname} {selectedPayer.lname}
                    <span className="text-gray-500 text-sm"> · {selectedPayer.phone}</span>
                  </span>
                  <button
                    onClick={() => setSelectedPayer(null)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search registrants by name or phone..."
                    className="w-full border border-gray-300 p-2"
                    value={payerQuery}
                    onChange={(e) => setPayerQuery(e.target.value)}
                  />
                  <div className="border border-t-0 border-gray-300 max-h-40 overflow-y-auto">
                    {filteredPayers.length > 0 ? (
                      filteredPayers.map((p) => (
                        <button
                          key={p.uuid}
                          onClick={() => setSelectedPayer(p)}
                          className="block w-full text-left px-2 py-1 text-sm hover:bg-gray-100"
                        >
                          {p.fname} {p.lname}
                          <span className="text-gray-500"> · {p.phone}</span>
                          {Number(p.unpaid_count) > 0 && (
                            <span className="text-gray-500"> · {p.unpaid_count} unpaid</span>
                          )}
                        </button>
                      ))
                    ) : (
                      <p className="px-2 py-2 text-sm text-gray-500">No matching registrants.</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Amount and date */}
            <div className="mb-4 flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full border border-gray-300 p-2"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date received</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 p-2"
                  value={dateReceived}
                  onChange={(e) => setDateReceived(e.target.value)}
                />
              </div>
            </div>

            {/* Cars */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apply to cars
              </label>
              <p className="text-xs text-gray-500 mb-1">
                One check can cover cars registered under different names.
              </p>
              <input
                type="text"
                placeholder="Search all unpaid cars by number, owner, or vehicle..."
                className="w-full border border-gray-300 p-2"
                value={carQuery}
                onChange={(e) => setCarQuery(e.target.value)}
              />
              <div className="border border-t-0 border-gray-300 max-h-60 overflow-y-auto">
                {visibleCars.length > 0 ? (
                  visibleCars.map((c) => (
                    <label
                      key={c.car_id}
                      className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-gray-100 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCarIds.includes(c.car_id)}
                        onChange={() => toggleCar(c.car_id)}
                      />
                      <span className="font-medium w-10">#{c.car_number}</span>
                      <span className="flex-1">
                        {c.year} {c.make} {c.model}
                      </span>
                      <span className="text-gray-500">
                        {c.owner_first_name} {c.owner_last_name}
                      </span>
                    </label>
                  ))
                ) : (
                  <p className="px-2 py-2 text-sm text-gray-500">No matching unpaid cars.</p>
                )}
              </div>
            </div>

            {/* Tally */}
            <div
              className={`text-sm rounded px-3 py-2 ${
                selectedCarIds.length === 0
                  ? 'bg-gray-100 text-gray-600'
                  : amountMatches
                    ? 'bg-green-100 text-green-800'
                    : 'bg-amber-100 text-amber-800'
              }`}
            >
              {selectedCarIds.length === 0 ? (
                <>Select at least one car.</>
              ) : (
                <>
                  Allocated ${expected.toFixed(2)} of ${amountIsValid ? enteredAmount.toFixed(2) : '0.00'}
                  {' '}({selectedCarIds.length} car{selectedCarIds.length === 1 ? '' : 's'} × ${entryFee})
                  {amountIsValid && !amountMatches && ' — amount does not match; saving anyway is fine.'}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between w-full mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!canSave}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Saving...' : 'Save Check'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddCheckModal;
